"use client";

import React from "react";

export interface TherapistWelcomeBannerLabels {
  headline?: string;
  acceptingBoth?: string;
  /** Fallback template interpolated with `{type}` (lowercased
   *  "individual" or "couples") — English-only. Consumers that need
   *  translations should pass `acceptingOnlyIndividual` +
   *  `acceptingOnlyCouples` instead, since embedding a raw English
   *  DB value inside a translated sentence never reads right (e.g.
   *  "J'accepte de nouveaux clients individual uniquement"). */
  acceptingOnly?: string;
  acceptingOnlyIndividual?: string;
  acceptingOnlyCouples?: string;
  nextOpening?: string;
  notAccepting?: string;
  cardIndividualLabel?: string;
  cardCouplesLabel?: string;
  cardTherapySuffix?: string;
  cardVirtualLabel?: string;
  cardVirtualSuffix?: string;
  cardInsuranceLabel?: string;
  cardInsuranceSuffix?: string;
  cardOntarioLabel?: string;
  cardOntarioSuffix?: string;
  notAcceptingCouplesBadge?: string;
  coveredByEmployer?: string;
  /** Interpolated with `{amount}` (raw dollar amount) and `{duration}`
   *  (session length copy). */
  startingFrom?: string;
}

export interface TherapistWelcomeBannerProps {
  firstName: string;
  acceptingClients: boolean;
  acceptingFor: string[];
  nextAvailable?: string;
  bookingNote?: string;
  rateRange: string;
  sessionDuration: string;
  clientTypes: string[];
  therapistTier: "resident" | "associate";
  credentialsWithFullNames?: { type: string; fullName: string }[];
  employmentStatus?: string;
  credentials?: string[];
  isApprovedForCouples?: boolean;
  isAcceptingCouples?: boolean;
  /** Hide price and show "Covered by employer" for MHAP users */
  isEmployerCovered?: boolean;
  /** Drop the rate information entirely from the additional-info bar.
   *  Used by imwell, where sessions are paid through the school benefit
   *  and dollar-amounts shouldn't surface on the profile. */
  hideRateInfo?: boolean;
  /** Optional label overrides for user-visible strings. Each field
   *  defaults to English; consumers that need translations pass a
   *  `labels` prop built from their i18n bundle. Other apps can omit
   *  and get the original copy. */
  labels?: TherapistWelcomeBannerLabels;
}

export function TherapistWelcomeBanner({
  firstName,
  acceptingClients,
  acceptingFor,
  nextAvailable,
  bookingNote,
  rateRange,
  sessionDuration,
  clientTypes,
  therapistTier,
  credentialsWithFullNames = [],
  employmentStatus,
  credentials = [],
  isApprovedForCouples = false,
  isAcceptingCouples = false,
  isEmployerCovered = false,
  hideRateInfo = false,
  labels
}: TherapistWelcomeBannerProps) {
  // Check if therapist is a contractor with only RSW credential
  const isContractorWithOnlyRSW =
    employmentStatus === 'CONTRACTOR' &&
    credentials.length === 1 &&
    credentials[0] === 'RSW';
  const headlineLabel = labels?.headline ?? "Let's work together";
  const acceptingBothLabel =
    labels?.acceptingBoth ?? 'Accepting individual & couples clients';
  const acceptingOnlyLabel = (rawType: string): string => {
    // Prefer per-type labels when the caller provides them (the only
    // way translations can read naturally — see JSDoc on the labels
    // interface). Fall back to the interpolation template for callers
    // that pass a generic label. Match on the raw DB value ("Individual"
    // / "Couples") case-insensitively so future capitalisation drift
    // doesn't break the lookup.
    const key = rawType.toLowerCase();
    if (key === 'individual' && labels?.acceptingOnlyIndividual) {
      return labels.acceptingOnlyIndividual;
    }
    if (key === 'couples' && labels?.acceptingOnlyCouples) {
      return labels.acceptingOnlyCouples;
    }
    const template = labels?.acceptingOnly ?? 'Accepting {type} clients only';
    return template.replace('{type}', key);
  };
  const nextOpeningLabel = labels?.nextOpening ?? 'Next opening';
  const notAcceptingLabel =
    labels?.notAccepting ?? 'Not currently accepting new clients';
  const cardIndividualLabel = labels?.cardIndividualLabel ?? 'Individual';
  const cardCouplesLabel = labels?.cardCouplesLabel ?? 'Couples';
  const cardTherapySuffix = labels?.cardTherapySuffix ?? 'Therapy';
  const cardVirtualLabel = labels?.cardVirtualLabel ?? 'Virtual';
  const cardVirtualSuffix = labels?.cardVirtualSuffix ?? 'Sessions';
  const cardInsuranceLabel = labels?.cardInsuranceLabel ?? 'Insurance';
  const cardInsuranceSuffix = labels?.cardInsuranceSuffix ?? 'Covered';
  const cardOntarioLabel = labels?.cardOntarioLabel ?? 'Ontario';
  const cardOntarioSuffix = labels?.cardOntarioSuffix ?? 'Only';
  const notAcceptingCouplesBadge =
    labels?.notAcceptingCouplesBadge ?? 'Not accepting';
  const coveredByEmployerLabel =
    labels?.coveredByEmployer ?? 'Covered by employer';
  const startingFromLabel = (amount: string, duration: string): string => {
    const template =
      labels?.startingFrom ?? 'Starting from ${amount} / {duration} session';
    return template.replace('{amount}', amount).replace('{duration}', duration);
  };
  return (
    <div className="bg-white rounded-2xl shadow-sm p-8">
      <div className="space-y-6">
        {/* Header with availability */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">{headlineLabel}</h2>
            <div className="flex items-center gap-3 flex-wrap">
              {acceptingClients && acceptingFor.length > 0 ? (
                <>
                  <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-800 px-4 py-2 rounded-full text-sm font-semibold">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    {acceptingFor.length === 2
                      ? acceptingBothLabel
                      : acceptingOnlyLabel(acceptingFor[0]!)}
                  </div>
                  {nextAvailable && (
                    <span className="text-sm text-gray-500">{nextOpeningLabel}: {nextAvailable}</span>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2 rounded-full text-sm font-semibold">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {notAcceptingLabel}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Service offerings grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {/* Individual Therapy */}
          <div className="bg-gray-50 rounded-xl p-4 text-center">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center mx-auto mb-2 shadow-sm">
              <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <p className="text-xs font-medium text-gray-900">{cardIndividualLabel}</p>
            <p className="text-xs text-gray-500">{cardTherapySuffix}</p>
          </div>
          
          {clientTypes.includes("Couples") && (
            <div className="bg-gray-50 rounded-xl p-4 text-center relative">
              {/* Show badge if approved for couples but not currently accepting */}
              {isApprovedForCouples && !isAcceptingCouples && (
                <div className="absolute -top-2 -right-2 bg-amber-100 border border-amber-300 text-amber-800 text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap">
                  {notAcceptingCouplesBadge}
                </div>
              )}
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center mx-auto mb-2 shadow-sm">
                <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <p className="text-xs font-medium text-gray-900">{cardCouplesLabel}</p>
              <p className="text-xs text-gray-500">{cardTherapySuffix}</p>
            </div>
          )}
          
          {/* Virtual */}
          <div className="bg-gray-50 rounded-xl p-4 text-center">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center mx-auto mb-2 shadow-sm">
              <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-xs font-medium text-gray-900">{cardVirtualLabel}</p>
            <p className="text-xs text-gray-500">{cardVirtualSuffix}</p>
          </div>

          {/* Insurance */}
          <div className="bg-gray-50 rounded-xl p-4 text-center">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center mx-auto mb-2 shadow-sm">
              <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-xs font-medium text-gray-900">{cardInsuranceLabel}</p>
            <p className="text-xs text-gray-500">{cardInsuranceSuffix}</p>
          </div>

          {/* Ontario Only - for RSW contractors */}
          {isContractorWithOnlyRSW && (
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center mx-auto mb-2 shadow-sm">
                <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <p className="text-xs font-medium text-gray-900">{cardOntarioLabel}</p>
              <p className="text-xs text-gray-500">{cardOntarioSuffix}</p>
            </div>
          )}
        </div>
        
        {/* Additional info bar */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          {bookingNote ? (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{bookingNote}</span>
            </div>
          ) : (
            <div></div>
          )}
          {hideRateInfo ? null : (
            <div className="text-sm text-gray-600">
              {isEmployerCovered ? (
                <span className="text-green-600 font-medium">{coveredByEmployerLabel}</span>
              ) : (
                <>
                  {startingFromLabel(
                    therapistTier === 'resident' ? '45' : '115',
                    sessionDuration,
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}